/*
  Collection "Load more" plus back-navigation restore.

  Products are appended client side, so returning from a product page used to
  drop everything past the first page: the browser re-requested the collection
  and rendered a single page of results at the top of the list. We now record
  how far the shopper had loaded and which product they opened, then rebuild
  that state and scroll them back to it.

  State is per collection URL (filters and sort included) and lives in
  sessionStorage, so it never leaks between tabs or between filter changes.
*/

const COLLECTION_STATE_PREFIX = 'adb:collection-state:';
const COLLECTION_STATE_TTL = 30 * 60 * 1000;

function collectionStateKey() {
  const url = new URL(window.location.href);

  // `page` is the one parameter that must not take part in the identity of the
  // list, otherwise returning to page 3 would look like a different collection.
  url.searchParams.delete('page');

  return COLLECTION_STATE_PREFIX + url.pathname + url.search;
}

function readCollectionState() {
  try {
    const raw = sessionStorage.getItem(collectionStateKey());

    if (!raw) {
      return null;
    }

    const state = JSON.parse(raw);

    if (!state || Date.now() - state.ts > COLLECTION_STATE_TTL) {
      sessionStorage.removeItem(collectionStateKey());

      return null;
    }

    return state;
  } catch (error) {
    // Private browsing and blocked storage both throw here; the page still works.
    return null;
  }
}

/*
  Only a Back/Forward step or a reload should rebuild the list. Arriving from a
  nav link or a fresh search is a new browse and belongs at the top, otherwise
  tapping "Engagement Rings" in the menu would drop the shopper halfway down a
  list they opened an hour ago.
*/
function isReturningNavigation() {
  try {
    const [entry] = performance.getEntriesByType('navigation');

    if (entry && entry.type) {
      return entry.type === 'back_forward' || entry.type === 'reload';
    }

    // Safari below 15 only has the deprecated API.
    const legacy = performance.navigation && performance.navigation.type;

    return legacy === 2 || legacy === 1;
  } catch (error) {
    return false;
  }
}

function writeCollectionState(patch) {
  try {
    const current = readCollectionState() || {};

    sessionStorage.setItem(
      collectionStateKey(),
      JSON.stringify({ ...current, ...patch, ts: Date.now() })
    );
  } catch (error) {
    /* storage unavailable or full: restoring is a nicety, never a requirement */
  }
}

if (!customElements.get('collection-load-more')) {
  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();

      this.btnLoadMore = this.querySelector('.js-btn-load-more'); /* check */
      this.productGrid = document.querySelector('#product-grid'); /* check */

      this.setProperties();
      this.toggleProductsVisibility();

      window.addEventListener('shopify:section:load', () => {
        this.setProperties();
        this.toggleProductsVisibility();
      });

      this.btnLoadMore.addEventListener('click', e => {
        e.preventDefault();

        this.btnLoadMore.setAttribute('disabled', true);

        this.loadMoreProducts();
      });

      if (this.cardText) {
        window.addEventListener('resize', () => {
          // this.setGridColsNumber();

          if (window.innerWidth < mobileWidth) {
            this.resetHiddenProducts();

            return;
          }

          this.toggleProductsVisibility();
        });
      }

      this.watchForDeparture();
      this.restoreFromState();
    }

    setProperties() {
      this.productGrid = document.querySelector('#product-grid');
      this.cardText = this.productGrid.querySelector('.js-card-text');
      this.btnLoadMore = this.querySelector('.js-btn-load-more');
      this.hiddenProducts = [];
      this.cardTextIndexOffset = 2;
      this.isLastPage = this.classList.contains('hidden');
      this.cardProduct = document.querySelector('card-product');
      this.startPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.lastLoadedPage = this.startPage;
      this.setGridColsNumber();
    }

    setGridColsNumber() {
      // prettier-ignore
      this.productGridCols = Math.round(this.productGrid.getBoundingClientRect().width / this.cardProduct?.getBoundingClientRect().width);
    }

    toggleProductsVisibility() {
      if (!this.cardText || this.isLastPage) {
        return;
      }

      const items = Array.from(this.productGrid.children);
      const visibleItems = items
        .filter(item => !item.classList.contains('hidden'))
        .reverse();
      const productRect = visibleItems
        .find(item => !item.classList.contains('js-card-text'))
        .getBoundingClientRect();
      const lastItem = visibleItems[0];

      const productGridRect =
        this.productGrid.getBoundingClientRect();
      const lastItemRect = lastItem.getBoundingClientRect();

      const productGridCols = Math.round(
        productGridRect.width / productRect.width
      );
      const productGridOffset =
        productGridRect.x + productGridRect.width;
      const lastItemOffset = lastItemRect.x + lastItemRect.width;

      const isLastItemCardText =
        items[items.length - 1].classList.contains('js-card-text');

      if (isLastItemCardText) {
        const prevProduct = visibleItems[1];
        const prevProductRect = prevProduct.getBoundingClientRect();
        const prevProductOffset =
          prevProductRect.x + prevProductRect.width;

        if (
          lastItemRect.y !== prevProductRect.y &&
          productGridOffset !== prevProductOffset
        ) {
          this.hiddenProducts[0].classList.remove('hidden');
          this.hiddenProducts.shift();
        }
      }

      const isLastColumn = productGridOffset === lastItemOffset;

      if (isLastColumn) {
        if (this.hiddenProducts.length >= productGridCols) {
          this.resetHiddenProducts(productGridCols);
        }

        return;
      }

      // prettier-ignore
      const gridGaps = Math.round((productGridRect.width - (lastItemOffset - productGridRect.x)) / productRect.width);

      if (this.hiddenProducts.length >= gridGaps) {
        const hiddenProductsLimit =
          this.hiddenProducts.length - gridGaps;

        this.resetHiddenProducts(hiddenProductsLimit);

        return;
      }

      let productsLooped = 0;

      for (const product of visibleItems) {
        if (product.classList.contains('js-card-text')) {
          continue;
        }

        productsLooped++;
        this.hiddenProducts.unshift(product);
        product.classList.add('hidden');

        if (productsLooped === productGridCols - gridGaps) {
          break;
        }
      }
    }

    resetHiddenProducts(limit) {
      if (!this.cardText) {
        return;
      }

      const hiddenProducts = [...this.hiddenProducts];
      let i = 0;

      for (const product of hiddenProducts) {
        i++;

        product.classList.remove('hidden');
        this.hiddenProducts.shift();

        if (limit && limit === i) {
          break;
        }
      }
    }

    async fetchPage(href) {
      const response = await fetch(href);
      const text = await response.text();

      return new DOMParser().parseFromString(text, 'text/html');
    }

    /* Appends one fetched page to the grid and adopts its "load more" state. */
    appendPage(nextPageHTML) {
      const productGridContainer = nextPageHTML.querySelector(
        '#ProductGridContainer'
      );
      const productGrid =
        productGridContainer.querySelector('#product-grid');
      const loadMoreContainer = productGridContainer.querySelector(
        'collection-load-more'
      );
      const btnLoadMore = loadMoreContainer.querySelector(
        '.js-btn-load-more'
      );

      const isLoadMoreHidden =
        loadMoreContainer.classList.contains('hidden');
      const products = productGrid.children;

      if (this.cardText) {
        if (this.cardTextIndexOffset > 0) {
          const productsArray = Array.from(products);
          const cardTextIndex = productsArray.findIndex(product =>
            product.classList.contains('js-card-text')
          );
          const elementAfterCardText =
            products[cardTextIndex - this.cardTextIndexOffset];

          this.cardTextIndexOffset = 0;

          if (elementAfterCardText) {
            const cardText = productsArray[cardTextIndex];

            cardText.remove();
            elementAfterCardText.insertAdjacentElement(
              'beforebegin',
              cardText
            );
          }
        } else {
          this.cardTextIndexOffset = 2;
        }
      }

      this.resetHiddenProducts();
      this.productGrid.insertAdjacentHTML(
        'beforeend',
        productGrid.innerHTML
      );

      this.lastLoadedPage += 1;

      if (isLoadMoreHidden) {
        this.isLastPage = true;
        this.classList.add('hidden');

        return;
      }

      this.toggleProductsVisibility();
      this.btnLoadMore.dataset.href = btnLoadMore.dataset.href;
    }

    async loadMoreProducts() {
      try {
        const nextPageHTML = await this.fetchPage(
          `${this.btnLoadMore.dataset.href}`
        );

        this.appendPage(nextPageHTML);

        // The URL deliberately keeps no `page` parameter. Pointing it at the
        // next page used to mean a shopper pressing Back landed on a single
        // orphaned page of results instead of the list they had built up.
        writeCollectionState({
          startPage: this.startPage,
          endPage: this.lastLoadedPage
        });
      } catch (error) {
        console.error(error);

        alert(error.message);
      } finally {
        this.btnLoadMore.removeAttribute('disabled');
      }
    }

    /* Builds a page URL from the current one, keeping filters and sorting. */
    pageHref(page) {
      const reference =
        this.btnLoadMore?.dataset.href || window.location.href;
      const url = new URL(reference, window.location.origin);

      url.searchParams.set('page', String(page));

      return url.pathname + url.search;
    }

    /* Remembers where the shopper was before they leave the collection. */
    watchForDeparture() {
      this.productGrid.addEventListener('click', event => {
        const link = event.target.closest('a[href*="/products/"]');

        if (!link) {
          return;
        }

        writeCollectionState({
          startPage: this.startPage,
          endPage: this.lastLoadedPage,
          productHref: link.getAttribute('href').split('?')[0],
          scrollY: Math.round(window.scrollY),
          pendingReturn: true
        });
      });

      window.addEventListener('pageshow', event => {
        if (!event.persisted) {
          return;
        }

        const state = readCollectionState();

        if (!state || typeof state.scrollY !== 'number') {
          return;
        }

        // Only step in when the browser clearly dropped the offset, so a
        // correct restore is never fought.
        if (window.scrollY > 100 || state.scrollY < 500) {
          return;
        }

        writeCollectionState({ pendingReturn: false });
        this.scrollToSaved(state);
      });

      // Covers leaving by any other route: browser back, a nav link, a share.
      window.addEventListener('pagehide', () => {
        writeCollectionState({
          startPage: this.startPage,
          endPage: this.lastLoadedPage,
          scrollY: Math.round(window.scrollY)
        });
      });
    }

    async restoreFromState() {
      const state = readCollectionState();

      if (!state || state.startPage !== this.startPage) {
        return;
      }

      // `pendingReturn` is set when the shopper opens a product and cleared
      // once used. Safari reports a bfcache-miss back step as 'navigate'
      // rather than 'back_forward', so the navigation type alone is not
      // enough to recognise a return on iOS.
      if (!isReturningNavigation() && !state.pendingReturn) {
        return;
      }

      writeCollectionState({ pendingReturn: false });

      // Nothing to do for a back/forward cache restore: the constructor does
      // not run again there, the DOM and scroll position are already intact.

      // Stop the browser restoring its own stale offset mid-rebuild.
      const previousScrollRestoration = history.scrollRestoration;

      try {
        history.scrollRestoration = 'manual';
      } catch (error) {
        /* not supported: the browser keeps its default behaviour */
      }

      const targetPage = state.endPage || this.startPage;

      if (targetPage > this.lastLoadedPage) {
        this.setRestoring(true);

        try {
          await this.loadPagesUpTo(targetPage);
        } catch (error) {
          console.error(error);
        } finally {
          this.setRestoring(false);
        }
      }

      this.scrollToSaved(state);

      try {
        history.scrollRestoration = previousScrollRestoration || 'auto';
      } catch (error) {
        /* see above */
      }
    }

    async loadPagesUpTo(targetPage) {
      const hrefs = [];

      for (let page = this.lastLoadedPage + 1; page <= targetPage; page++) {
        hrefs.push(this.pageHref(page));
      }

      if (!hrefs.length) {
        return;
      }

      // Fetched together so a shopper who had loaded six pages waits for one
      // round trip rather than six.
      const pages = await Promise.all(
        hrefs.map(href => this.fetchPage(href))
      );

      pages.forEach(page => {
        if (!this.isLastPage) {
          this.appendPage(page);
        }
      });
    }

    setRestoring(isRestoring) {
      this.classList.toggle('is-restoring', isRestoring);

      if (!this.btnLoadMore) {
        return;
      }

      if (isRestoring) {
        this.btnLoadMore.setAttribute('disabled', true);
      } else {
        this.btnLoadMore.removeAttribute('disabled');
      }
    }

    scrollToSaved(state) {
      const links = Array.from(
        this.productGrid.querySelectorAll('a[href*="/products/"]')
      );
      const match = links.find(
        link => link.getAttribute('href').split('?')[0] === state.productHref
      );

      let card = match;

      while (card && card.parentElement !== this.productGrid) {
        card = card.parentElement;
      }

      if (!card && !(typeof state.scrollY === 'number' && state.scrollY > 0)) {
        return;
      }

      // `scroll-behavior: smooth` is set globally on <html>. Left in place the
      // shopper would watch the page travel back down the whole list, so it is
      // suspended for the jump rather than relying on `behavior: 'instant'`,
      // which older Safari ignores.
      const root = document.documentElement;
      const previousBehaviour = root.style.scrollBehavior;

      root.style.scrollBehavior = 'auto';

      if (card) {
        card.scrollIntoView({ block: 'center' });
      } else {
        window.scrollTo(0, state.scrollY);
      }

      requestAnimationFrame(() => {
        root.style.scrollBehavior = previousBehaviour;
      });
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
}
